import { useCallback, useEffect, useRef, useState } from 'react';
import {
	Tooltip,
	Legend,
	BarChart,
	Bar,
	XAxis,
	YAxis,
	CartesianGrid,
	ResponsiveContainer,
	LabelList,
	Line,
	LineChart,
	AreaChart,
	Area,
} from 'recharts';
import TableLoading from '../../../components/TableLoading';
import { notifyError } from '../../../services/notify';
import { mapTimeRangeToApiFilterAlt3, request } from '../../../services/utilities';
import { analyticsAPI } from '../../../services/constants';
import * as moment from 'moment';
import SlidingPane from '../../../components/SlidingPane';
import { Card, Col, Row, Statistic } from 'antd';
import AnalysisBystaff from '../../../components/Modals/frontDesk/staffAnalyticsmodal';
import { TimeRangeDropdownAlt3 } from '../../../components/TimeRangeDropdownAlt3';
import { MetricCardAlt } from '../../../components/MetricCardAlt';

const StaffProcedureOverview = ({ selectedStaff }) => {
	const [resources, setResources] = useState();
	const [specialist, setSpecialist] = useState({});
	const [trends, setTrends] = useState([]);
	const [doctors, setDoctors] = useState();
	const [statusTrends, setStatusTrends] = useState([]);
	const [consumables, setConsumables] = useState([]);
	const [procedureType, setProcedureType] = useState();
	const [procedureDrug, setProcedureDrug] = useState([]);
	const [drugTrend, setDrugTrend] = useState([]);
	const [requestMetrics, setRequestMetrics] = useState();
	const [modal, setModal] = useState(false);
	const [modalAlt, setModalAlt] = useState(false);
	const [modalThird, setModalThird] = useState(false);

	const [loading, setLoading] = useState({
		resources: false,
		specialist: false,
		trends: false,
		doctors: false,
		statusTrends: false,
		consumables: false,
		type: false,
		drug: false,
		metrics: false,
		drugTrend: false,
		staffRank: false,
	});

	const [timeRanges, setTimeRanges] = useState({
		resources: 'past30Days',
		specialist: 'past30Days',
		trends: 'past30Days',
		doctors: 'past30Days',
		statusTrends: 'past30Days',
		consumables: 'past30Days',
		type: 'past30Days',
		drug: 'past30Days',
		metrics: 'past30Days',
		drugTrend: 'past30Days',
		staffRank: 'past30Days',
	});

	const fetchRequestMetrics = async range => {
		setLoading(prev => ({ ...prev, metrics: true }));
		try {
			const dateFilter = mapTimeRangeToApiFilterAlt3(range);
			const url = `${analyticsAPI}/patient-request-metrics?dateFilter=${dateFilter}&requestType=procedure&staffId=${selectedStaff?.id}`;
			const res = await request(url, 'GET', true);
			console.log(res, 'responces...');
			setRequestMetrics(res?.result || {});
		} catch (error) {
			console.error('Failed to fetch request metrics:', error);
			setRequestMetrics({});
		} finally {
			setLoading(prev => ({ ...prev, metrics: false }));
		}
	};

	const fetchResources = async range => {
		setLoading(prev => ({ ...prev, resources: true }));
		try {
			const dateFilter = mapTimeRangeToApiFilterAlt3(range);
			const url = `${analyticsAPI}/procedure-resources?dateFilter=${dateFilter}&staffId=${selectedStaff?.id}`;
			const res = await request(url, 'GET', true);
			const apiResponse =
				res?.solutionObj && typeof res.solutionObj === 'object'
					? Object.entries(res.solutionObj).map(([name, count]) => ({
							name,
							count,
					  }))
					: [];
			setResources(apiResponse);
		} catch (error) {
			console.error('Failed to fetch resources:', error);
			setResources(0);
		} finally {
			setLoading(prev => ({ ...prev, resources: false }));
		}
	};

	const fetchSpecialist = async range => {
		setLoading(prev => ({ ...prev, specialist: true }));
		try {
			const dateFilter = mapTimeRangeToApiFilterAlt3(range);
			const url = `${analyticsAPI}/procedure-specialist-count?dateFilter=${dateFilter}&staffId=${selectedStaff?.id}`;
			const res = await request(url, 'GET', true);
			const apiResponse =
				res?.result && typeof res.result === 'object'
					? Object.entries(res.result).map(([name, count]) => ({
							name,
							count,
					  }))
					: [];
			setSpecialist(apiResponse);
		} catch (error) {
			console.error('Failed to fetch specialist count:', error);
			setSpecialist(0);
		} finally {
			setLoading(prev => ({ ...prev, specialist: false }));
		}
	};

	const fetchTrends = async range => {
		setLoading(prev => ({ ...prev, trends: true }));
		try {
			const dateFilter = mapTimeRangeToApiFilterAlt3(range);
			const url = `${analyticsAPI}/procedure-trends?dateFilter=${dateFilter}&staffId=${selectedStaff?.id}`;
			const res = await request(url, 'GET', true);
			const apiResponse = res?.result?.map(x => ({
				name:
					x?.date ||
					(dateFilter === 'past-three-years' && x?.month && moment(x?.month).format('MMMM, YY')) ||
					(x?.month && moment(x?.month).format('MMMM')) ||
					x?.day ||
					x?.week ||
					x?.year ||
					x?.hour,
				value: parseInt(x?.count, 10),
			}));
			setTrends(apiResponse || []);
		} catch (error) {
			console.error('Failed to fetch procedure trends:', error);
			setTrends([]);
		} finally {
			setLoading(prev => ({ ...prev, trends: false }));
		}
	};

	const fetchDoctors = async range => {
		setLoading(prev => ({ ...prev, doctors: true }));
		try {
			const dateFilter = mapTimeRangeToApiFilterAlt3(range);
			const url = `${analyticsAPI}/procedure-doctors?dateFilter=${dateFilter}&staffId=${selectedStaff?.id}`;
			const res = await request(url, 'GET', true);
			const apiResponse = res?.result?.map(x => ({
				name: x?.createdBy,
				count: parseInt(x?.count, 10),
			}));
			setDoctors(apiResponse);
		} catch (error) {
			console.error('Failed to fetch doctors count:', error);
			setDoctors(0);
		} finally {
			setLoading(prev => ({ ...prev, doctors: false }));
		}
	};

	const fetchStatusTrends = async range => {
		setLoading(prev => ({ ...prev, statusTrends: true }));
		try {
			const dateFilter = mapTimeRangeToApiFilterAlt3(range);
			const url = `${analyticsAPI}/procedure-status-trend?dateFilter=${dateFilter}&staffId=${selectedStaff?.id}`;
			const res = await request(url, 'GET', true);
			const apiResponse = Array.isArray(res?.result)
				? res.result.map(item => ({
						name: item?.year || 'N/A',
						count: Number(item?.count) || 0,
						completed: Number(item?.completedCount) || 0,
						pending: Number(item?.pendingCount) || 0,
						deleted: Number(item?.deletedCount) || 0,
				  }))
				: [];
			console.log(apiResponse, 'this is apiResponse');
			setStatusTrends(apiResponse);
		} catch (error) {
			console.error('Failed to fetch status trends:', error);
			setStatusTrends([]);
		} finally {
			setLoading(prev => ({ ...prev, statusTrends: false }));
		}
	};

	const fetchConsumables = async range => {
		setLoading(prev => ({ ...prev, consumables: true }));
		try {
			const dateFilter = mapTimeRangeToApiFilterAlt3(range);
			const url = `${analyticsAPI}/procedure-consumable?dateFilter=${dateFilter}&staffId=${selectedStaff?.id}`;
			const res = await request(url, 'GET', true);
			setConsumables(res?.results || 0);
		} catch (error) {
			console.error('Failed to fetch consumables:', error);
			setConsumables(0);
		} finally {
			setLoading(prev => ({ ...prev, consumables: false }));
		}
	};

	const fetchProcedureType = async range => {
		setLoading(prev => ({ ...prev, type: true }));
		try {
			const dateFilter = mapTimeRangeToApiFilterAlt3(range);
			const url = `${analyticsAPI}/procedure-type?dateFilter=${dateFilter}&staffId=${selectedStaff?.id}`;
			const res = await request(url, 'GET', true);
			const apiResponse = res?.result?.map(x => ({
				name: x?.name,
				count: parseInt(x?.count, 10),
			}));
			setProcedureType(apiResponse || 0);
		} catch (error) {
			console.error('Failed to fetch procedure type:', error);
			setProcedureType(0);
		} finally {
			setLoading(prev => ({ ...prev, type: false }));
		}
	};

	const fetchProcedureDrug = async range => {
		setLoading(prev => ({ ...prev, drug: true }));
		try {
			const dateFilter = mapTimeRangeToApiFilterAlt3(range);
			const url = `${analyticsAPI}/procedure-drug?dateFilter=${dateFilter}&staffId=${selectedStaff?.id}`;
			const res = await request(url, 'GET', true);
			const apiResponse = res?.result?.map(x => ({
				name: x?.name,
				count: parseInt(x?.count, 10),
			}));
			setProcedureDrug(apiResponse);
		} catch (error) {
			console.error('Failed to fetch procedure drug:', error);
			setProcedureDrug(0);
		} finally {
			setLoading(prev => ({ ...prev, drug: false }));
		}
	};

	const fetchDrugTrend = async range => {
		setLoading(prev => ({ ...prev, drugTrend: true }));
		try {
			const dateFilter = mapTimeRangeToApiFilterAlt3(range);
			const url = `${analyticsAPI}/procedure-drug-trend?dateFilter=${dateFilter}&staffId=${selectedStaff?.id}`;
			const res = await request(url, 'GET', true);
			const apiResponse = res?.result?.map(x => ({
				name: x?.category_name || 'unknown',
				count: parseInt(x?.count, 10),
				date: x?.date || (x?.month && moment(x?.month).format('MMMM')) || x?.day || x?.week || x?.year || x?.hour,
			}));
			setDrugTrend(apiResponse || []);
		} catch (error) {
			console.error('Failed to fetch drug trend:', error);
			setDrugTrend([]);
		} finally {
			setLoading(prev => ({ ...prev, drugTrend: false }));
		}
	};

	const handleTimeRangeChange = (range, chartId) => {
		setTimeRanges(prev => ({ ...prev, [chartId]: range }));

		switch (chartId) {
			case 'metrics':
				fetchRequestMetrics(range);
				break;
			case 'resources':
				fetchResources(range);
				break;
			case 'specialist':
				fetchSpecialist(range);
				break;
			case 'trends':
				fetchTrends(range);
				break;
			case 'doctors':
				fetchDoctors(range);
				break;
			case 'statusTrends':
				fetchStatusTrends(range);
				break;
			case 'consumables':
				fetchConsumables(range);
				break;
			case 'type':
				fetchProcedureType(range);
				break;
			case 'drug':
				fetchProcedureDrug(range);
				break;
			case 'drugTrend':
				fetchDrugTrend(range);
				break;

			default:
				break;
		}
	};

	useEffect(() => {
		const loadAll = async () => {
			try {
				await Promise.all([
					fetchRequestMetrics(timeRanges.metrics),
					fetchResources(timeRanges.resources),
					fetchSpecialist(timeRanges.specialist),
					fetchTrends(timeRanges.trends),
					fetchDoctors(timeRanges.doctors),
					fetchStatusTrends(timeRanges.statusTrends),
					fetchConsumables(timeRanges.consumables),
					fetchProcedureType(timeRanges.type),
					fetchProcedureDrug(timeRanges.drug),
					fetchDrugTrend(timeRanges.drugTrend),
				]);
			} catch (error) {
				console.error('Error loading initial procedure analytics:', error);
			}
		};

		loadAll();
	}, []);

	const renderCustomLabelAlt = ({ x, y, value, fill }) => {
		return (
			<text x={x} y={y} dy={-6} textAnchor="middle" fill={fill || '#333'} fontSize={12}>
				{value?.toLocaleString()}
			</text>
		);
	};

	const formatBarLabel = ({ x, y, value, width }) => (
		<text x={x + width / 2} y={y - 6} textAnchor="middle" fill="#333" fontSize={12}>
			{value?.toLocaleString()}
		</text>
	);

	console.log(statusTrends, 'status-trends...');
	return (
		<div className="content-">
			<div className="content-bo">
				<AnalysisBystaff module="procedure" />

				<div className="row">
					{/* Overview Metrics */}
					<div className="col-sm-12">
						<div className="element-wrapper">
							<h6 className="element-header">Key Performance Indicators</h6>
							<div className="form-inline justify-content-end mb-4">
								<TimeRangeDropdownAlt3 chartId="metrics" onChange={handleTimeRangeChange} value={timeRanges.metrics} />
							</div>
							<p className="chart-description mb-3 p-2 card">Summary metrics showing overall procedure activity and engagement levels.</p>
							<div className="element-content">
								<div className="row">
									{loading.metrics ? (
										<div className="d-flex justify-content-center align-items-center w-100 element-box" style={{ minHeight: '200px' }}>
											<TableLoading />
										</div>
									) : (
										requestMetrics && (
											<div className="row">
												{Object.entries(requestMetrics).map(([key, value], idx) => {
													if (['success'].includes(key)) return null;

													const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());

													const color = `hsl(${key.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0) % 360}, 70%, 50%)`;

													return (
														<MetricCardAlt
															key={key}
															metricKey={key}
															label={label}
															value={value}
															total={requestMetrics?.totalRequests}
															color={color}
															currentRange={timeRanges.metrics}
														/>
													);
												})}
											</div>
										)
									)}
								</div>
							</div>
						</div>
					</div>

					{/* Procedure Trends*/}
					<div className="col-sm-12">
						<div className="element-wrapper">
							<h6 className="element-header"> Procedure Trends </h6>
							<div className="form-inline justify-content-end mb-4">
								<TimeRangeDropdownAlt3 chartId="trends" onChange={handleTimeRangeChange} value={timeRanges?.trends} />
							</div>
							<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
								This presents an overview of procedure counts over time, tracking frequency and distribution to highlight emerging trends
								and peak activity periods.
							</p>
							<div className="row">
								<div className="col-md-12 mb-4">
									<div className="card h-100">
										<div className="card-body">
											{loading.trends ? (
												<TableLoading />
											) : trends?.length > 0 ? (
												<div style={{ width: '100%', height: 400 }}>
													<ResponsiveContainer>
														<AreaChart data={trends} margin={{ top: 20, right: 30, left: 20, bottom: 55 }}>
															<CartesianGrid strokeDasharray="3 3" />
															<XAxis
																dataKey={'name'}
																angle={-45}
																textAnchor="end"
																fontSize={12}
																interval={timeRanges?.trends == 'past-quarter' ? 2 : 0}
																tickFormatter={value => {
																	const truncated = value.length > 15 ? value.substring(0, 15) + '...' : value;
																	const capitalized = truncated
																		.toLowerCase()
																		.split(' ')
																		.map(word => word.charAt(0).toUpperCase() + word.slice(1))
																		.join(' ');
																	return capitalized;
																}}
															/>
															<YAxis domain={[0, 'auto']} />
															<Tooltip
																formatter={value => Number(value).toLocaleString()}
																labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
															/>
															<Area
																type="monotone"
																dataKey="value"
																stroke="#22BB33"
																fill="#22BB33"
																strokeWidth={2}
																dot={{ r: 4 }}
																activeDot={{ r: 6 }}
															>
																<LabelList
																	dataKey="value"
																	content={renderCustomLabelAlt}
																	position="top"
																	style={{ fontSize: '12px', fill: '#333' }}
																/>
															</Area>
														</AreaChart>
													</ResponsiveContainer>
												</div>
											) : (
												<div className="text-center fs-bold">No Record Found!</div>
											)}
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>

					{/* Procedure Drug Trends*/}
					<div className="col-sm-12">
						<div className="element-wrapper">
							<h6 className="element-header"> Procedure Drug Trends </h6>
							<div className="form-inline justify-content-end mb-4">
								<TimeRangeDropdownAlt3 chartId="drugTrend" onChange={handleTimeRangeChange} value={timeRanges?.drugTrend} />
							</div>
							<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
								Shows how procedure-related drug usage changes over time, helping to identify patterns, peak demand periods, and emerging
								treatment trends.
							</p>
							<div className="row">
								<div className="col-md-12 mb-4">
									<div className="card h-100">
										<div className="card-body">
											{loading.drugTrend ? (
												<TableLoading />
											) : drugTrend?.length > 0 ? (
												<div style={{ width: '100%', height: 400 }}>
													<ResponsiveContainer>
														<LineChart data={drugTrend} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
															<CartesianGrid strokeDasharray="3 3" />
															<XAxis
																dataKey={'name'}
																angle={-45}
																textAnchor="end"
																fontSize={12}
																interval={timeRanges?.drugTrend == 'past-quarter' ? 2 : 0}
																tickFormatter={value => {
																	const truncated = value.length > 15 ? value.substring(0, 15) + '...' : value;
																	const capitalized = truncated
																		.toLowerCase()
																		.split(' ')
																		.map(word => word.charAt(0).toUpperCase() + word.slice(1))
																		.join(' ');
																	return capitalized;
																}}
															/>
															<YAxis domain={[0, 'auto']} />
															<Tooltip
																formatter={value => Number(value).toLocaleString()}
																labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
															/>
															<Line
																type="monotone"
																dataKey="count"
																stroke="#22BB33"
																fill="#22BB33"
																strokeWidth={2}
																dot={{ r: 4 }}
																activeDot={{ r: 6 }}
															></Line>
														</LineChart>
													</ResponsiveContainer>
												</div>
											) : (
												<div className="text-center fs-bold">No Record Found!</div>
											)}
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>

					{/* Resources Count */}
					<div className="col-sm-12 col-lg-6 mt-4">
						<div className="element-wrapper">
							<h6 className="element-header">Procedure Resource Overview</h6>
							<div className=" d-flex justify-content-end mt-5 t-5 align-items-center">
								<TimeRangeDropdownAlt3 chartId="resources" onChange={handleTimeRangeChange} value={timeRanges?.resources} />
							</div>
							<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
								This chart illustrates equipment usage distribution, highlighting the frequency and utilization patterns of various medical
								devices during procedures.
							</p>
							<div className="row">
								<div className="col-md-12 mb-4">
									<div className="card">
										{loading.resources ? (
											<TableLoading />
										) : resources?.length > 0 ? (
											<div style={{ width: '100%', height: 400 }}>
												<ResponsiveContainer width="100%" height={400}>
													<LineChart data={resources} margin={{ top: 20, right: 30, left: 20, bottom: 55 }}>
														<CartesianGrid strokeDasharray="3 3" />
														<XAxis
															dataKey={'name'}
															angle={-45}
															textAnchor="end"
															fontSize={12}
															interval={0}
															tickFormatter={value => {
																const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
																const capitalized = truncated
																	.toLowerCase()
																	.split(' ')
																	.map(word => word.charAt(0).toUpperCase() + word.slice(1))
																	.join(' ');
																return capitalized;
															}}
														/>
														<YAxis domain={[0, 'auto']} />
														<Tooltip
															formatter={value => Number(value).toLocaleString()}
															labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
														/>
														<Line type="monotone" dataKey="count" stroke="#82ca9d" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }}>
															<LabelList
																dataKey="count"
																content={renderCustomLabelAlt}
																position="top"
																style={{ fontSize: '10px', fill: '#333' }}
															/>
														</Line>
													</LineChart>
												</ResponsiveContainer>
											</div>
										) : (
											<div className="text-center fs-bold">No Record Found!</div>
										)}
									</div>
								</div>
							</div>
						</div>
					</div>

					{/* Specialist  Count */}
					<div className="col-sm-12 col-lg-6 mt-4">
						<div className="element-wrapper">
							<h6 className="element-header">Specialist Count Overview</h6>
							<div className=" d-flex justify-content-end mt-5 t-5 align-items-center">
								<TimeRangeDropdownAlt3 chartId="specialist" onChange={handleTimeRangeChange} value={timeRanges?.specialist} />
							</div>
							<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
								The Specialist Chart illustrates the distribution of procedure counts across various specialties.
							</p>
							<div className="row">
								<div className="col-md-12 mb-4">
									<div className="card">
										{loading.specialist ? (
											<TableLoading />
										) : specialist?.length > 0 ? (
											<div style={{ width: '100%', height: 400 }}>
												<ResponsiveContainer width="100%" height={400}>
													<LineChart data={specialist} margin={{ top: 20, right: 20, left: 20, bottom: 55 }}>
														<CartesianGrid strokeDasharray="3 3" />
														<XAxis
															dataKey={'name'}
															angle={-45}
															textAnchor="end"
															fontSize={12}
															interval={0}
															tickFormatter={value => {
																const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
																const capitalized = truncated
																	.toLowerCase()
																	.split(' ')
																	.map(word => word.charAt(0).toUpperCase() + word.slice(1))
																	.join(' ');
																return capitalized;
															}}
														/>
														<YAxis domain={[0, 'auto']} />
														<Tooltip
															formatter={value => Number(value).toLocaleString()}
															labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
														/>
														<Line type="monotone" dataKey="count" stroke="#FF8042" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }}>
															<LabelList
																dataKey="count"
																content={renderCustomLabelAlt}
																position="top"
																style={{ fontSize: '10px', fill: '#333' }}
															/>
														</Line>
													</LineChart>
												</ResponsiveContainer>
											</div>
										) : (
											<div className="text-center fs-bold">No Record Found!</div>
										)}
									</div>
								</div>
							</div>
						</div>
					</div>

					{/* Procedure Top10 Doctors Overview */}
					<div className="col-sm-12 col-lg-6 mt-4">
						<div className="element-wrapper">
							<h6 className="element-header mb-5"> Procedure By Doctors (Top 10)</h6>
							<br />
							<br />

							<p
								className="chart-description  card p-2"
								style={{ fontSize: '14px', color: '#666', marginBottom: '10px', paddingTop: '200px' }}
							>
								The Top 10 Doctors Chart highlights the performance of the most active doctors based on procedure counts, showcasing their
								significant contributions to patient care.
							</p>
							<div className="row">
								<div className="col-md-12 mb-4">
									<div className="card">
										{loading.doctors ? (
											<TableLoading />
										) : doctors?.length > 0 ? (
											<div style={{ width: '100%', height: 400 }}>
												<ResponsiveContainer width="100%" height={400}>
													<LineChart data={doctors?.slice(0, 10)} margin={{ top: 20, right: 30, left: 20, bottom: 55 }}>
														<CartesianGrid strokeDasharray="3 3" />
														<XAxis
															dataKey={'name'}
															angle={-45}
															textAnchor="end"
															fontSize={12}
															interval={0}
															tickFormatter={value => {
																const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
																const capitalized = truncated
																	.toLowerCase()
																	.split(' ')
																	.map(word => word.charAt(0).toUpperCase() + word.slice(1))
																	.join(' ');
																return capitalized;
															}}
														/>
														<YAxis domain={[0, 'auto']} />
														<Tooltip
															formatter={value => Number(value).toLocaleString()}
															labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
														/>
														<Line type="monotone" dataKey="count" stroke="#82ca9d" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }}>
															<LabelList
																dataKey="count"
																content={renderCustomLabelAlt}
																position="top"
																style={{ fontSize: '10px', fill: '#333' }}
															/>
														</Line>
													</LineChart>
												</ResponsiveContainer>
											</div>
										) : (
											<div className="text-center fs-bold">No Record Found!</div>
										)}
									</div>
								</div>
							</div>
						</div>
					</div>

					{/* Procedure Bottom 10 Doctors Overview */}
					<div className="col-sm-12 col-lg-6 mt-4">
						<div className="element-wrapper">
							<h6 className="element-header"> Procedure By Doctors (Bottom 10 )</h6>

							<div className="d-flex justify-content-end w-100">
								<div>
									<div
										style={{ width: '100px', cursor: 'pointer', textAlign: 'center' }}
										onClick={() => setModal(true)}
										className="form-control form-control-sm text-white bg-primary rounded"
									>
										View All
									</div>
								</div>
								&nbsp;
								<TimeRangeDropdownAlt3 chartId="doctors" onChange={handleTimeRangeChange} value={timeRanges?.doctors} />
							</div>
							<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
								The Bottom 10 Doctors Chart highlights the performance of the least active doctors based on procedure counts, offering
								insights into areas with lower contribution to patient care.
							</p>
							<div className="row">
								<div className="col-md-12 mb-4">
									<div className="card">
										{loading.doctors ? (
											<TableLoading />
										) : doctors?.length > 0 ? (
											<div style={{ width: '100%', height: 400 }}>
												<ResponsiveContainer width="100%" height={400}>
													<LineChart data={doctors?.slice(-10)} margin={{ top: 20, right: 30, left: 20, bottom: 55 }}>
														<CartesianGrid strokeDasharray="3 3" />
														<XAxis
															dataKey={'name'}
															angle={-45}
															textAnchor="end"
															fontSize={12}
															interval={0}
															tickFormatter={value => {
																const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
																const capitalized = truncated
																	.toLowerCase()
																	.split(' ')
																	.map(word => word.charAt(0).toUpperCase() + word.slice(1))
																	.join(' ');
																return capitalized;
															}}
														/>
														<YAxis domain={[0, 'auto']} />
														<Tooltip
															formatter={value => Number(value).toLocaleString()}
															labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
														/>
														<Line type="monotone" dataKey="count" stroke="#82ca9d" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }}>
															<LabelList
																dataKey="count"
																content={renderCustomLabelAlt}
																position="top"
																style={{ fontSize: '10px', fill: '#333' }}
															/>
														</Line>
													</LineChart>
												</ResponsiveContainer>
											</div>
										) : (
											<div className="text-center fs-bold">No Record Found!</div>
										)}
									</div>
								</div>
							</div>
						</div>
						<SlidingPane isOpen={modal}>
							<div className=" content-box">
								<div className=" d-flex justify-content-end w-100">
									<button className="btn btn-danger float-end" onClick={() => setModal(false)}>
										&times;
									</button>
								</div>

								<div className=" d-flex justify-content-end mt-5 t-5 align-items-center">
									<TimeRangeDropdownAlt3 chartId="doctors" onChange={handleTimeRangeChange} value={timeRanges?.doctors} />
								</div>
								<div className="row  w-100 " style={{ paddingLeft: '50px', paddingRight: '50px' }}>
									<div className="col-sm-12">
										<div className="element-wrapper">
											<h6 className="element-header">Procedure By Doctors</h6>

											<p className="chart-description card p-2 " style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
												High-level metrics showing overall metrics of reading over time.
											</p>
											<div className="element-box">
												<Row gutter={16}>
													<Col span={8}>
														<Card>
															<Statistic title="Total" value={doctors?.length} suffix="" />
															<small className="text-muted">Total Doctors</small>
														</Card>
													</Col>
												</Row>
											</div>
										</div>
									</div>
								</div>

								<div className=" " style={{ marginLeft: '50px', marginRight: '50px' }}>
									<p className="chart-description card p-2">
										This chart highlights the performance of active doctors based on procedure counts, offering insights into areas with
										lower and higher contribution to patient care.
									</p>
								</div>

								<div className=" " style={{ marginLeft: '50px', marginRight: '50px' }}>
									<div className=" card w-100 ">
										<div className="row">
											<div className="col-md-12 mb-4">
												<div className="card h-100">
													<div className="card-body">
														{loading.doctors ? (
															<TableLoading />
														) : doctors?.length > 0 ? (
															<div style={{ width: '100%', height: 'auto' }}>
																{doctors?.length > 40 ? (
																	<ResponsiveContainer width="100%" height={doctors?.length * 30}>
																		<LineChart
																			data={doctors}
																			layout="vertical"
																			margin={{ top: 20, right: 30, left: 100, bottom: 55 }} // extra left margin for names
																		>
																			<CartesianGrid strokeDasharray="3 3" />

																			{/* Value axis (count) */}
																			<XAxis type="number" domain={[0, 'auto']} />

																			{/* Category axis (doctor names) */}
																			<YAxis
																				dataKey="name"
																				type="category"
																				width={120} // adjust for long names
																				tickFormatter={value => {
																					const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
																					const capitalized = truncated
																						.toLowerCase()
																						.split(' ')
																						.map(word => word.charAt(0).toUpperCase() + word.slice(1))
																						.join(' ');
																					return capitalized;
																				}}
																			/>

																			<Tooltip
																				formatter={value => Number(value).toLocaleString()}
																				labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
																			/>

																			<Legend wrapperStyle={{ marginBottom: '-45px' }} />

																			<Line
																				type="monotone"
																				dataKey="count"
																				stroke="#82ca9d"
																				strokeWidth={2}
																				dot={{ r: 4 }}
																				activeDot={{ r: 6 }}
																			>
																				<LabelList
																					dataKey="count"
																					position="right" // labels at the end of the line
																					content={renderCustomLabelAlt}
																					style={{ fontSize: '10px', fill: '#333' }}
																				/>
																			</Line>
																		</LineChart>
																	</ResponsiveContainer>
																) : (
																	<ResponsiveContainer width="100%" height={400}>
																		<LineChart data={doctors} margin={{ top: 20, right: 30, left: 20, bottom: 55 }}>
																			<CartesianGrid strokeDasharray="3 3" />
																			<XAxis
																				dataKey={'name'}
																				angle={-45}
																				textAnchor="end"
																				fontSize={12}
																				interval={0}
																				tickFormatter={value => {
																					const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
																					const capitalized = truncated
																						.toLowerCase()
																						.split(' ')
																						.map(word => word.charAt(0).toUpperCase() + word.slice(1))
																						.join(' ');
																					return capitalized;
																				}}
																			/>
																			<YAxis domain={[0, 'auto']} />
																			<Tooltip
																				formatter={value => Number(value).toLocaleString()}
																				labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
																			/>
																			<Legend wrapperStyle={{ marginBottom: '-45px' }} />
																			<Line
																				type="monotone"
																				dataKey="count"
																				stroke="#82ca9d"
																				strokeWidth={2}
																				dot={{ r: 4 }}
																				activeDot={{ r: 6 }}
																			>
																				<LabelList
																					dataKey="count"
																					content={renderCustomLabelAlt}
																					position="top"
																					style={{ fontSize: '10px', fill: '#333' }}
																				/>
																			</Line>
																		</LineChart>
																	</ResponsiveContainer>
																)}
															</div>
														) : (
															<div className="text-center fs-bold">No Record Found!</div>
														)}
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</SlidingPane>
					</div>

					{/*Procedure Status Trend */}
					<div className="col-12 mt-4">
						<div className="element-wrapper">
							<h6 className="element-header"> Procedure Status Trend</h6>
							<div className=" d-flex justify-content-end mt-5 t-5 align-items-center">
								<TimeRangeDropdownAlt3 chartId="statusTrends" onChange={handleTimeRangeChange} value={timeRanges?.statusTrends} />
							</div>
							<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
								The Procedure Status Trends Chart displays the distribution of procedure statuses based on the applied filter, tracking the
								proportion of pending versus completed procedures.
							</p>
							<div className="row ">
								<div className="col-12 mb-4">
									<div className="card">
										{loading.statusTrends ? (
											<TableLoading />
										) : statusTrends && statusTrends.length > 0 ? (
											<div style={{ width: '100%', height: 400 }}>
												<ResponsiveContainer width="100%" height={400}>
													<AreaChart data={statusTrends} margin={{ top: 40, right: 30, left: 20, bottom: 60 }}>
														<defs>
															<linearGradient id="colorPending" x1="0" y1="0" x2="0" y2="1">
																<stop offset="5%" stopColor="#FFA726" stopOpacity={0.8} />
																<stop offset="95%" stopColor="#FFA726" stopOpacity={0} />
															</linearGradient>

															<linearGradient id="colorCompleted" x1="0" y1="0" x2="0" y2="1">
																<stop offset="5%" stopColor="#66BB6A" stopOpacity={0.8} />
																<stop offset="95%" stopColor="#66BB6A" stopOpacity={0} />
															</linearGradient>

															<linearGradient id="colorDeleted" x1="0" y1="0" x2="0" y2="1">
																<stop offset="5%" stopColor="#EF5350" stopOpacity={0.8} />
																<stop offset="95%" stopColor="#EF5350" stopOpacity={0} />
															</linearGradient>
														</defs>

														<CartesianGrid strokeDasharray="3 3" />

														<XAxis
															dataKey="name"
															angle={-45}
															textAnchor="end"
															fontSize={12}
															tickMargin={10}
															interval={0}
															label={{
																// value: 'Year',
																position: 'insideBottom',
																offset: -45,
																fontSize: 13,
															}}
														/>

														<YAxis
															fontSize={12}
															domain={[0, 'auto']}
															label={{
																value: 'Count',
																angle: -90,
																position: 'insideLeft',
																offset: 10,
																fontSize: 13,
															}}
														/>

														<Tooltip
															content={({ active, payload, label }) => {
																if (active && payload && payload.length) {
																	const data = payload[0].payload;
																	return (
																		<div
																			style={{
																				background: '#fff',
																				padding: '10px',
																				border: '1px solid #ccc',
																				borderRadius: '6px',
																				boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
																			}}
																		>
																			<p style={{ marginBottom: '4px', fontWeight: 'bold' }}>Year: {label}</p>
																			<p style={{ color: '#FFA726', margin: '2px 0' }}>Pending: {data.pending?.toLocaleString()}</p>
																			<p style={{ color: '#66BB6A', margin: '2px 0' }}>Completed: {data.completed?.toLocaleString()}</p>
																			<p style={{ color: '#EF5350', margin: '2px 0' }}>Deleted: {data.deleted?.toLocaleString()}</p>
																			<hr style={{ margin: '6px 0', borderTop: '1px solid #eee' }} />
																			<p style={{ margin: '0', color: '#333' }}>
																				<strong>Total:</strong> {data.count?.toLocaleString()}
																			</p>
																		</div>
																	);
																}
																return null;
															}}
														/>

														<Legend verticalAlign="bottom" height={36} wrapperStyle={{ marginBottom: '-45px' }} />

														<Area
															type="monotone"
															dataKey="pending"
															stroke="#FFA726"
															fill="url(#colorPending)"
															name="Pending"
															connectNulls
															strokeWidth={2}
															activeDot={{ r: 6 }}
														/>

														<Area
															type="monotone"
															dataKey="completed"
															stroke="#66BB6A"
															fill="url(#colorCompleted)"
															name="Completed"
															connectNulls
															strokeWidth={2}
															activeDot={{ r: 6 }}
														/>

														<Area
															type="monotone"
															dataKey="deleted"
															stroke="#EF5350"
															fill="url(#colorDeleted)"
															name="Deleted"
															connectNulls
															strokeWidth={2}
															activeDot={{ r: 6 }}
														/>
													</AreaChart>
												</ResponsiveContainer>
											</div>
										) : (
											<div className="text-center fw-bold">No Record Found!</div>
										)}
									</div>
								</div>
							</div>
						</div>
					</div>

					{/*Procedure Consumables */}
					<div className="col-12 mt-4">
						<div className="element-wrapper">
							<h6 className="element-header">Procedure Consumables</h6>
							<div className=" d-flex justify-content-end mt-5 t-5 align-items-center">
								<TimeRangeDropdownAlt3 chartId="consumables" onChange={handleTimeRangeChange} value={timeRanges?.consumables} />
							</div>
							<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
								The Procedure Consumables Chart illustrates the usage trends of various consumable items required for medical procedures,
								based on the applied filter.
							</p>
							<div className="row">
								<div className="col-md-12 mb-4">
									<div className="card">
										{loading.consumables ? (
											<TableLoading />
										) : consumables?.length > 0 ? (
											<div style={{ width: '100%', height: 400 }}>
												<ResponsiveContainer>
													<BarChart data={consumables} layout="horizontal" margin={{ top: 20, right: 20, left: 20, bottom: 55 }}>
														<CartesianGrid strokeDasharray="3 3" />
														<XAxis
															dataKey={'name'}
															angle={-45}
															textAnchor="end"
															fontSize={12}
															interval={0}
															tickFormatter={value => {
																const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
																const capitalized = truncated
																	.toLowerCase()
																	.split(' ')
																	.map(word => word.charAt(0).toUpperCase() + word.slice(1))
																	.join(' ');
																return capitalized;
															}}
														/>
														<YAxis domain={[0, 'auto']} />
														<Tooltip
															formatter={value => Number(value).toLocaleString()}
															labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
														/>

														<Legend wrapperStyle={{ marginBottom: '-45px' }} />
														<Bar dataKey="count" fill="#00C49F" name="Count">
															<LabelList
																dataKey="count"
																content={formatBarLabel}
																position="top"
																style={{ fontSize: '12px', fill: '#333' }}
															/>
														</Bar>
													</BarChart>
												</ResponsiveContainer>
											</div>
										) : (
											<div className="text-center fs-bold">No Record Found!</div>
										)}
									</div>
								</div>
							</div>
						</div>
					</div>

					{/*Top 10 Procedure Type */}
					<div className="col-sm-12 col-lg-6 mt-4">
						<div className="element-wrapper">
							<h6 className="element-header">Procedure Service (Top 10)</h6>

							<p
								className="chart-description card p-2"
								style={{ fontSize: '14px', color: '#666', marginBottom: '10px', marginTop: '90px' }}
							>
								This chart highlights the top procedure services, providing insights into high-demand areas and supporting resource planning
								and operational focus within the healthcare setting.
							</p>
							<div className="row">
								<div className="col-md-12 mb-4">
									<div className="card">
										{loading.type ? (
											<TableLoading />
										) : procedureType?.length > 0 ? (
											<div style={{ width: '100%', height: 400 }}>
												<ResponsiveContainer width="100%" height={400}>
													<AreaChart data={procedureType?.slice(0, 10)} margin={{ top: 20, right: 30, left: 20, bottom: 55 }}>
														<CartesianGrid strokeDasharray="3 3" />
														<XAxis
															dataKey={'name'}
															angle={-45}
															textAnchor="end"
															fontSize={12}
															interval={0}
															tickFormatter={value => {
																const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
																const capitalized = truncated
																	.toLowerCase()
																	.split(' ')
																	.map(word => word.charAt(0).toUpperCase() + word.slice(1))
																	.join(' ');
																return capitalized;
															}}
														/>
														<YAxis domain={[0, 'auto']} />
														<Tooltip
															formatter={value => Number(value).toLocaleString()}
															labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
														/>
														<Area
															type="monotone"
															dataKey="count"
															stroke="#0088FE"
															fill="#0088FE"
															strokeWidth={2}
															dot={{ r: 4 }}
															activeDot={{ r: 6 }}
														>
															<LabelList
																dataKey="count"
																content={renderCustomLabelAlt}
																position="top"
																style={{ fontSize: '12px', fill: '#333' }}
															/>
														</Area>
													</AreaChart>
												</ResponsiveContainer>
											</div>
										) : (
											<div className="text-center fs-bold">No Record Found!</div>
										)}
									</div>
								</div>
							</div>
						</div>
					</div>

					{/*Bottom 10 Procedure Type */}
					<div className="col-sm-12 col-lg-6 mt-4">
						<div className="element-wrapper">
							<h6 className="element-header">Procedure Service (Bottom 10)</h6>
							<div className="d-flex justify-content-end w-100">
								<div>
									<div
										style={{ width: '100px', cursor: 'pointer', textAlign: 'center' }}
										onClick={() => setModalAlt(true)}
										className="form-control form-control-sm text-white bg-primary rounded"
									>
										View All
									</div>
								</div>
								&nbsp;
								<TimeRangeDropdownAlt3 chartId="type" onChange={handleTimeRangeChange} value={timeRanges?.type} />
							</div>
							<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
								This chart identifies low-demanding procedure service, offering opportunities to assess resource utilization and address
								potential inefficiencies in the healthcare workflow.
							</p>
							<div className="row">
								<div className="col-md-12 mb-4">
									<div className="card">
										{loading.type ? (
											<TableLoading />
										) : procedureType?.length > 0 ? (
											<div style={{ width: '100%', height: 400 }}>
												<ResponsiveContainer width="100%" height={400}>
													<AreaChart data={procedureType?.slice(-10)} margin={{ top: 20, right: 30, left: 20, bottom: 55 }}>
														<CartesianGrid strokeDasharray="3 3" />
														<XAxis
															dataKey={'name'}
															angle={-45}
															textAnchor="end"
															fontSize={12}
															interval={0}
															tickFormatter={value => {
																const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
																const capitalized = truncated
																	.toLowerCase()
																	.split(' ')
																	.map(word => word.charAt(0).toUpperCase() + word.slice(1))
																	.join(' ');
																return capitalized;
															}}
														/>
														<YAxis domain={[0, 'auto']} />
														<Tooltip
															formatter={value => Number(value).toLocaleString()}
															labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
														/>
														<Area
															type="monotone"
															dataKey="count"
															stroke="#0088FE"
															fill="#0088FE"
															strokeWidth={2}
															dot={{ r: 4 }}
															activeDot={{ r: 6 }}
														>
															<LabelList
																dataKey="count"
																content={renderCustomLabelAlt}
																position="top"
																style={{ fontSize: '12px', fill: '#333' }}
															/>
														</Area>
													</AreaChart>
												</ResponsiveContainer>
											</div>
										) : (
											<div className="text-center fs-bold">No Record Found!</div>
										)}
									</div>
								</div>
							</div>
						</div>
						<SlidingPane isOpen={modalAlt}>
							<div className=" content-box">
								<div className=" d-flex justify-content-end w-100">
									<button className="btn btn-danger float-end" onClick={() => setModalAlt(false)}>
										&times;
									</button>
								</div>

								<div className=" d-flex justify-content-end mt-5 t-5 align-items-center">
									<TimeRangeDropdownAlt3 chartId="type" onChange={handleTimeRangeChange} value={timeRanges?.type} />
								</div>
								<div className="row  w-100 " style={{ paddingLeft: '50px', paddingRight: '50px' }}>
									<div className="col-sm-12">
										<div className="element-wrapper">
											<h6 className="element-header">Procedure Service</h6>

											<p className="chart-description card p-2 " style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
												High-level metrics showing overall metrics of reading over time.
											</p>
											<div className="element-box">
												<Row gutter={16}>
													<Col span={8}>
														<Card>
															<Statistic title="Total " value={procedureType?.length} suffix="" />
															<small className="text-muted">Average Transactions</small>
														</Card>
													</Col>
												</Row>
											</div>
										</div>
									</div>
								</div>

								<div className=" " style={{ marginLeft: '50px', marginRight: '50px' }}>
									<p className="chart-description card p-2">
										This chart identifies procedure service based on demand, offering opportunities to assess resource utilization and
										address potential inefficiencies in the healthcare workflow.
									</p>
								</div>

								<div className=" " style={{ marginLeft: '50px', marginRight: '50px' }}>
									<div className=" card w-100 ">
										<div className="row">
											<div className="col-md-12 mb-4">
												<div className="card h-100">
													<div className="card-body">
														{loading.type ? (
															<TableLoading />
														) : procedureType?.length > 0 ? (
															<div style={{ width: '100%', height: 'auto' }}>
																{procedureType?.length > 45 ? (
																	<ResponsiveContainer width="100%" height={procedureType?.length * 30}>
																		<AreaChart
																			data={procedureType}
																			layout="vertical"
																			margin={{ top: 20, right: 30, left: 100, bottom: 55 }} // left margin adjusted for labels
																		>
																			<CartesianGrid strokeDasharray="3 3" />

																			<XAxis type="number" domain={[0, 'auto']} />

																			<YAxis
																				dataKey="name"
																				type="category"
																				width={120} // adjust so labels fit
																				tickFormatter={value => {
																					const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
																					const capitalized = truncated
																						.toLowerCase()
																						.split(' ')
																						.map(word => word.charAt(0).toUpperCase() + word.slice(1))
																						.join(' ');
																					return capitalized;
																				}}
																			/>

																			<Tooltip
																				formatter={value => Number(value).toLocaleString()}
																				labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
																			/>

																			<Legend wrapperStyle={{ marginBottom: '-45px' }} />

																			<Area
																				type="monotone"
																				dataKey="count"
																				stroke="#0088FE"
																				fill="#0088FE"
																				strokeWidth={2}
																				dot={{ r: 4 }}
																				activeDot={{ r: 6 }}
																			>
																				<LabelList
																					dataKey="count"
																					position="right" // labels at the end of the area line instead of top
																					content={renderCustomLabelAlt}
																					style={{ fontSize: '12px', fill: '#333' }}
																				/>
																			</Area>
																		</AreaChart>
																	</ResponsiveContainer>
																) : (
																	<ResponsiveContainer width="100%" height={400}>
																		<AreaChart data={procedureType} margin={{ top: 20, right: 30, left: 20, bottom: 55 }}>
																			<CartesianGrid strokeDasharray="3 3" />
																			<XAxis
																				dataKey={'name'}
																				angle={-45}
																				textAnchor="end"
																				fontSize={12}
																				interval={0}
																				tickFormatter={value => {
																					const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
																					const capitalized = truncated
																						.toLowerCase()
																						.split(' ')
																						.map(word => word.charAt(0).toUpperCase() + word.slice(1))
																						.join(' ');
																					return capitalized;
																				}}
																			/>
																			<YAxis domain={[0, 'auto']} />
																			<Tooltip
																				formatter={value => Number(value).toLocaleString()}
																				labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
																			/>
																			<Legend wrapperStyle={{ marginBottom: '-45px' }} />
																			<Area
																				type="monotone"
																				dataKey="count"
																				stroke="#0088FE"
																				fill="#0088FE"
																				strokeWidth={2}
																				dot={{ r: 4 }}
																				activeDot={{ r: 6 }}
																			>
																				<LabelList
																					dataKey="count"
																					content={renderCustomLabelAlt}
																					position="top"
																					style={{ fontSize: '12px', fill: '#333' }}
																				/>
																			</Area>
																		</AreaChart>
																	</ResponsiveContainer>
																)}
															</div>
														) : (
															<div className="text-center fs-bold">No Record Found!</div>
														)}
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</SlidingPane>
					</div>

					{/*Top 10 Procedure Drug */}
					<div className="col-sm-12 col-lg-6 mt-4">
						<div className="element-wrapper">
							<h6 className="element-header">Procedure Drug (Top 10)</h6>

							<p
								className="chart-description card p-2"
								style={{ fontSize: '14px', color: '#666', marginBottom: '10px', marginTop: '90px' }}
							>
								This chart identifies high-demand medications, providing valuable insights for inventory management and ensuring adequate
								supply for effective healthcare delivery.
							</p>
							<div className="row">
								<div className="col-md-12 mb-4">
									<div className="card">
										{loading.drug ? (
											<TableLoading />
										) : procedureDrug?.length > 0 ? (
											<div style={{ width: '100%', height: 400 }}>
												<ResponsiveContainer width="100%" height={400}>
													<AreaChart data={procedureDrug?.slice(0, 10)} margin={{ top: 20, right: 30, left: 20, bottom: 55 }}>
														<CartesianGrid strokeDasharray="3 3" />
														<XAxis
															dataKey={'name'}
															angle={-45}
															textAnchor="end"
															fontSize={12}
															interval={0}
															tickFormatter={value => {
																const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
																const capitalized = truncated
																	.toLowerCase()
																	.split(' ')
																	.map(word => word.charAt(0).toUpperCase() + word.slice(1))
																	.join(' ');
																return capitalized;
															}}
														/>
														<YAxis domain={[0, 'auto']} />
														<Tooltip
															formatter={value => Number(value).toLocaleString()}
															labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
														/>
														<Legend wrapperStyle={{ marginBottom: '-45px' }} />
														<Area
															type="monotone"
															dataKey="count"
															stroke="#0088FE"
															fill="#0088FE"
															strokeWidth={2}
															dot={{ r: 4 }}
															activeDot={{ r: 6 }}
														>
															<LabelList
																dataKey="count"
																content={renderCustomLabelAlt}
																position="top"
																style={{ fontSize: '10px', fill: '#333' }}
															/>
														</Area>
													</AreaChart>
												</ResponsiveContainer>
											</div>
										) : (
											<div className="text-center fs-bold">No Record Found!</div>
										)}
									</div>
								</div>
							</div>
						</div>
					</div>

					{/*Bottom 10 Procedure Drug */}
					<div className="col-sm-12 col-lg-6 mt-4">
						<div className="element-wrapper">
							<h6 className="element-header">Procedure Drug (Bottom 10)</h6>

							<div className="d-flex justify-content-end w-100">
								<div>
									<div
										style={{ width: '100px', cursor: 'pointer', textAlign: 'center' }}
										onClick={() => setModalThird(true)}
										className="form-control form-control-sm text-white bg-primary rounded"
									>
										View All
									</div>
								</div>
								&nbsp;
								<TimeRangeDropdownAlt3 chartId="drug" onChange={handleTimeRangeChange} value={timeRanges?.drug} />
							</div>
							<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
								This chart reveals low-utilization medications, offering opportunities to optimize stock levels and address potential
								inefficiencies in drug resource allocation.
							</p>
							<div className="row">
								<div className="col-md-12 mb-4">
									<div className="card">
										{loading.drug ? (
											<TableLoading />
										) : procedureDrug?.length > 0 ? (
											<div style={{ width: '100%', height: 400 }}>
												<ResponsiveContainer width="100%" height={400}>
													<AreaChart data={procedureDrug?.slice(-10)} margin={{ top: 20, right: 30, left: 20, bottom: 55 }}>
														<CartesianGrid strokeDasharray="3 3" />
														<XAxis
															dataKey={'name'}
															angle={-45}
															textAnchor="end"
															fontSize={12}
															interval={0}
															tickFormatter={value => {
																const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
																const capitalized = truncated
																	.toLowerCase()
																	.split(' ')
																	.map(word => word.charAt(0).toUpperCase() + word.slice(1))
																	.join(' ');
																return capitalized;
															}}
														/>
														<YAxis domain={[0, 'auto']} />
														<Tooltip
															formatter={value => Number(value).toLocaleString()}
															labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
														/>
														<Legend wrapperStyle={{ marginBottom: '-45px' }} />
														<Area
															type="monotone"
															dataKey="count"
															stroke="#0088FE"
															fill="#0088FE"
															strokeWidth={2}
															dot={{ r: 4 }}
															activeDot={{ r: 6 }}
														>
															<LabelList
																dataKey="count"
																content={renderCustomLabelAlt}
																position="top"
																style={{ fontSize: '10px', fill: '#333' }}
															/>
														</Area>
													</AreaChart>
												</ResponsiveContainer>
											</div>
										) : (
											<div className="text-center fs-bold">No Record Found!</div>
										)}
									</div>
								</div>
							</div>
						</div>
						<SlidingPane isOpen={modalThird}>
							<div className=" content-box">
								<div className=" d-flex justify-content-end w-100">
									<button className="btn btn-danger float-end" onClick={() => setModalThird(false)}>
										&times;
									</button>
								</div>

								<div className=" d-flex justify-content-end mt-5 t-5 align-items-center">
									<TimeRangeDropdownAlt3 chartId="drug" onChange={handleTimeRangeChange} value={timeRanges?.drug} />
								</div>
								<div className="row  w-100 " style={{ paddingLeft: '50px', paddingRight: '50px' }}>
									<div className="col-sm-12">
										<div className="element-wrapper">
											<h6 className="element-header">Procedure Drug</h6>

											<p className="chart-description card p-2 " style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
												High-level metrics showing overall metrics of reading over time.
											</p>
											<div className="element-box">
												<Row gutter={16}>
													<Col span={8}>
														<Card>
															<Statistic title="Total" value={procedureDrug?.length} suffix="" />
															<small className="text-muted">Total Procedures</small>
														</Card>
													</Col>
												</Row>
											</div>
										</div>
									</div>
								</div>

								<div className=" " style={{ marginLeft: '50px', marginRight: '50px' }}>
									<p className="chart-description card p-2">
										This chart reveals the rate of utilization in medications, offering opportunities to optimize stock levels and address
										potential inefficiencies in drug resource allocation.
									</p>
								</div>

								<div className=" " style={{ marginLeft: '50px', marginRight: '50px' }}>
									<div className=" card w-100 ">
										<div className="row">
											<div className="col-md-12 mb-4">
												<div className="card h-100">
													<div className="card-body">
														{loading.drug ? (
															<TableLoading />
														) : procedureDrug?.length > 0 ? (
															<div style={{ width: '100%', height: 'auto' }}>
																{procedureDrug.length > 40 ? (
																	<ResponsiveContainer width="100%" height={procedureDrug?.length * 30}>
																		<AreaChart
																			data={procedureDrug}
																			layout="vertical"
																			margin={{ top: 20, right: 30, left: 100, bottom: 55 }} // extra left margin for labels
																		>
																			<CartesianGrid strokeDasharray="3 3" />

																			<XAxis type="number" domain={[0, 'auto']} />

																			<YAxis
																				dataKey="name"
																				type="category"
																				width={120} // adjust so labels fit
																				tickFormatter={value => {
																					const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
																					const capitalized = truncated
																						.toLowerCase()
																						.split(' ')
																						.map(word => word.charAt(0).toUpperCase() + word.slice(1))
																						.join(' ');
																					return capitalized;
																				}}
																			/>

																			<Tooltip
																				formatter={value => Number(value).toLocaleString()}
																				labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
																			/>

																			<Legend wrapperStyle={{ marginBottom: '-45px' }} />

																			<Area
																				type="monotone"
																				dataKey="count"
																				stroke="#0088FE"
																				fill="#0088FE"
																				strokeWidth={2}
																				dot={{ r: 4 }}
																				activeDot={{ r: 6 }}
																			>
																				<LabelList
																					dataKey="count"
																					position="right" // put numbers at the end of the area line
																					content={renderCustomLabelAlt}
																					style={{ fontSize: '10px', fill: '#333' }}
																				/>
																			</Area>
																		</AreaChart>
																	</ResponsiveContainer>
																) : (
																	<ResponsiveContainer width="100%" height={400}>
																		<AreaChart data={procedureDrug} margin={{ top: 20, right: 30, left: 20, bottom: 55 }}>
																			<CartesianGrid strokeDasharray="3 3" />
																			<XAxis
																				dataKey={'name'}
																				angle={-45}
																				textAnchor="end"
																				fontSize={12}
																				interval={0}
																				tickFormatter={value => {
																					const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
																					const capitalized = truncated
																						.toLowerCase()
																						.split(' ')
																						.map(word => word.charAt(0).toUpperCase() + word.slice(1))
																						.join(' ');
																					return capitalized;
																				}}
																			/>
																			<YAxis domain={[0, 'auto']} />
																			<Tooltip
																				formatter={value => Number(value).toLocaleString()}
																				labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
																			/>
																			<Legend wrapperStyle={{ marginBottom: '-45px' }} />
																			<Area
																				type="monotone"
																				dataKey="count"
																				stroke="#0088FE"
																				fill="#0088FE"
																				strokeWidth={2}
																				dot={{ r: 4 }}
																				activeDot={{ r: 6 }}
																			>
																				<LabelList
																					dataKey="count"
																					content={renderCustomLabelAlt}
																					position="top"
																					style={{ fontSize: '10px', fill: '#333' }}
																				/>
																			</Area>
																		</AreaChart>
																	</ResponsiveContainer>
																)}
															</div>
														) : (
															<div className="text-center fs-bold">No Record Found!</div>
														)}
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</SlidingPane>
					</div>
				</div>
			</div>
		</div>
	);
};

export default StaffProcedureOverview;
